import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Button } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { PageHeader, SubtitleM } from "../../components/Typography";
import useFetch from "../../hooks/useFetch";
import usePostRequest from "../../hooks/usePostRequest";
import useDeleteRequest from "../../hooks/useDeleteRequest";
import TabularLoadingPlaceholder from "../../components/LoadingPlaceholders/TabularLoadingPlaceholder";
import BottomActionBar from "../../components/BottomActionBar";
import Table from "../../components/Table";
import { DeleteIcon } from "../../components/utils/InsyncIconsUtil";

export default function AiFinetuning() {
  const tokens = useSelector((state) => state?.tokens?.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);

  const [urlPatternData, setUrlPatternData] = useState([]);
  const [pageHtmlPdfData, setPageHtmlPdfData] = useState([]);
  const [htmlTagsData, setHtmlTagsData] = useState([]);

  // Controls for enabling/disabling the Save button
  const isSaveDisabled = useRef(true);
  const [disabledSave, setDisabledSave] = useState(true);

  // Fetch existing data
  const {
    data: fetchedData,
    loading,
    error,
  } = useFetch(
    currentApp?.id
      ? `/v1/controls/list_finetunings?app_id=${currentApp.id}`
      : null,
    tokens,
    []
  );

  // Post request for upserting
  const { sendPostRequest } = usePostRequest(
    `/v1/controls/upsert_finetunings`,
    tokens
  );

  // Delete request (bulk)
  const { sendDeleteRequest } = useDeleteRequest(
    `/v1/controls/bulk_delete_finetunings`
  );

  // On load, separate the fetched data into categories
  useEffect(() => {
    if (Array.isArray(fetchedData)) {
      const urlPatternItems = [];
      const pageHtmlPdfItems = [];
      const htmlTagsItems = [];

      fetchedData.forEach((item) => {
        const baseItem = {
          ...item,
          isNew: false,
          hasChanges: false,
          deleted: false,
        };
        switch (item.finetuning_type) {
          case "url_pattern":
            urlPatternItems.push(baseItem);
            break;
          case "html_weightage":
          case "pdf_weightage":
            pageHtmlPdfItems.push(baseItem);
            break;
          case "html_tags_to_remove":
            htmlTagsItems.push(baseItem);
            break;
          default:
            // do nothing for unknown types
            break;
        }
      });

      setUrlPatternData(urlPatternItems);
      setPageHtmlPdfData(pageHtmlPdfItems);
      setHtmlTagsData(htmlTagsItems);

      // Initially no unsaved changes
      isSaveDisabled.current = true;
      setDisabledSave(true);
    }
  }, [fetchedData]);

  // Recompute disabledSave whenever data changes
  useEffect(() => {
    const anyUrlChanged = urlPatternData.some(
      (r) => r.isNew || r.hasChanges || r.deleted
    );
    const anyPdfChanged = pageHtmlPdfData.some(
      (r) => r.isNew || r.hasChanges || r.deleted
    );
    const anyTagsChanged = htmlTagsData.some(
      (r) => r.isNew || r.hasChanges || r.deleted
    );

    const disabled = !(anyUrlChanged || anyPdfChanged || anyTagsChanged);
    isSaveDisabled.current = disabled;
    setDisabledSave(disabled);
  }, [urlPatternData, pageHtmlPdfData, htmlTagsData]);

  /////////////////////////////////////////////////////////////////////////////
  // Mark a row as deleted in local state
  /////////////////////////////////////////////////////////////////////////////
  function handleDeleteRow(setterFn, rowId) {
    // console.log("Marking row as deleted. ID:", rowId);
    setterFn((prev) =>
      prev.map((item) => {
        const uniqueId = item.id ?? item.tempId;
        if (uniqueId === rowId) {
          return { ...item, deleted: true, hasChanges: true };
        }
        return item;
      })
    );

    // Immediately enable the Save button
    isSaveDisabled.current = false;
    setDisabledSave(false);
  }

  /////////////////////////////////////////////////////////////////////////////
  // Add new rows
  /////////////////////////////////////////////////////////////////////////////
  const handleAddUrlPattern = () => {
    isSaveDisabled.current = false;
    setDisabledSave(false);
    setUrlPatternData((prev) => [
      {
        tempId: uuidv4(),
        id: null,
        app_id: currentApp?.id,
        trigger_point: "",
        finetuning_type: "url_pattern",
        pattern: "",
        penalty: "",
        metadata: "",
        isNew: true,
        hasChanges: true,
        deleted: false,
      },
      ...prev,
    ]);
  };

  const handleAddPageHtmlPdf = () => {
    isSaveDisabled.current = false;
    setDisabledSave(false);
    setPageHtmlPdfData((prev) => [
      {
        tempId: uuidv4(),
        id: null,
        app_id: currentApp?.id,
        trigger_point: "",
        finetuning_type: "html_weightage",
        pattern: "",
        penalty: "",
        metadata: "",
        isNew: true,
        hasChanges: true,
        deleted: false,
      },
      ...prev,
    ]);
  };

  const handleAddHtmlTagsCleanup = () => {
    isSaveDisabled.current = false;
    setDisabledSave(false);
    setHtmlTagsData((prev) => [
      {
        tempId: uuidv4(),
        id: null,
        app_id: currentApp?.id,
        trigger_point: "",
        finetuning_type: "html_tags_to_remove",
        pattern: "",
        penalty: "",
        metadata: "",
        isNew: true,
        hasChanges: true,
        deleted: false,
      },
      ...prev,
    ]);
  };

  /////////////////////////////////////////////////////////////////////////////
  // Track field changes
  /////////////////////////////////////////////////////////////////////////////
  function handleFieldChange(setterFn, rowId, fieldName, newValue) {
    isSaveDisabled.current = false;
    setDisabledSave(false);

    setterFn((prev) =>
      prev.map((item) => {
        const uniqueId = item.id ?? item.tempId;
        if (uniqueId === rowId) {
          return {
            ...item,
            [fieldName]: newValue,
            hasChanges: true,
          };
        }
        return item;
      })
    );
  }

  /////////////////////////////////////////////////////////////////////////////
  // Check if row is empty for certain fields
  /////////////////////////////////////////////////////////////////////////////
  function isRowEmpty(row, requiredFields) {
    return requiredFields.every((field) => {
      const val = row[field];
      return !val || !val.toString().trim();
    });
  }

  /////////////////////////////////////////////////////////////////////////////
  // SAVE: Bulk-delete + upsert
  /////////////////////////////////////////////////////////////////////////////
  async function handleSave() {
    // 1) Collect changed rows
    const changedUrl = urlPatternData.filter((r) => r.isNew || r.hasChanges);
    const changedPdf = pageHtmlPdfData.filter((r) => r.isNew || r.hasChanges);
    const changedTags = htmlTagsData.filter((r) => r.isNew || r.hasChanges);

    // 2) Among changed, find those flagged as deleted
    const deletedUrl = changedUrl.filter((r) => r.deleted);
    const deletedPdf = changedPdf.filter((r) => r.deleted);
    const deletedTags = changedTags.filter((r) => r.deleted);

    const allDeleted = [...deletedUrl, ...deletedPdf, ...deletedTags];

    // 3) IDs that exist in the DB (i.e. not just brand-new rows)
    // filter out null or undefined
    const idsToDelete = allDeleted.map((r) => r.id).filter((id) => !!id);

    // 4) Non-deleted => upsert
    const rowsToUpsert = [...changedUrl, ...changedPdf, ...changedTags].filter(
      (r) => !r.deleted
    );

    // Filter out empty rows
    const filteredUpsert = rowsToUpsert.filter((row) => {
      switch (row.finetuning_type) {
        case "url_pattern":
        case "html_weightage":
        case "pdf_weightage":
          // These require trigger_point, pattern, penalty
          return !isRowEmpty(row, ["trigger_point", "pattern", "penalty"]);
        case "html_tags_to_remove":
          // Also needs metadata
          return !isRowEmpty(row, ["trigger_point", "metadata"]);
        default:
          // unknown type => just keep
          return true;
      }
    });

    // Convert empty "metadata" to {}
    const finalUpsertItems = filteredUpsert.map((row) => {
      if (typeof row.metadata === "string" && !row.metadata.trim()) {
        return { ...row, metadata: {} };
      }
      return row;
    });

    try {
      // 1) Bulk delete if needed
      if (idsToDelete.length > 0) {
        await sendDeleteRequest({
          app_id: currentApp?.id,
          ids: idsToDelete,
        });
      }

      // 2) Upsert the rest
      if (finalUpsertItems.length > 0) {
        const finetuningsPayload = finalUpsertItems.map(
          ({ isNew, hasChanges, deleted, tempId, ...rest }) => rest
        );
        await sendPostRequest({ finetunings: finetuningsPayload });
      }

      // 3) Clean up local state
      // remove the deleted items from state, reset flags on updated items.
      const upsertedUniqueIds = new Set(
        finalUpsertItems.map((x) => x.id ?? x.tempId)
      );

      setUrlPatternData((prev) =>
        prev
          // remove any that were flagged deleted
          .filter((item) => !item.deleted)
          .map((item) =>
            upsertedUniqueIds.has(item.id ?? item.tempId)
              ? { ...item, isNew: false, hasChanges: false, deleted: false }
              : item
          )
      );
      setPageHtmlPdfData((prev) =>
        prev
          .filter((item) => !item.deleted)
          .map((item) =>
            upsertedUniqueIds.has(item.id ?? item.tempId)
              ? { ...item, isNew: false, hasChanges: false, deleted: false }
              : item
          )
      );
      setHtmlTagsData((prev) =>
        prev
          .filter((item) => !item.deleted)
          .map((item) =>
            upsertedUniqueIds.has(item.id ?? item.tempId)
              ? { ...item, isNew: false, hasChanges: false, deleted: false }
              : item
          )
      );

      isSaveDisabled.current = true;
      setDisabledSave(true);
      // console.log("Save completed successfully.");
    } catch (err) {
      console.error("Error while saving finetunings:", err);
    }
  }

  /////////////////////////////////////////////////////////////////////////////
  // TABLE DEFINITIONS
  /////////////////////////////////////////////////////////////////////////////

  // 1) URL Patterns
  const urlPatternHeaders = [
    { id: "trigger_point", columnData: "Site" },
    { id: "pattern", columnData: "Pattern" },
    { id: "penalty", columnData: "Penalty" },
    { id: "actions", columnData: "Actions" },
  ];

  const urlPatternTableContents = urlPatternData
    // hide the deleted ones from the UI
    .filter((row) => !row.deleted)
    .map((row) => [
      {
        id: `trigger_point-${row.id ?? row.tempId}`,
        element: {
          type: "textfield",
          placeholder: "Enter Site",
          value: row.trigger_point || "",
          maxWidth: "100px",
          minWidth: "100px",
          onChange: (e) =>
            handleFieldChange(
              setUrlPatternData,
              row.id ?? row.tempId,
              "trigger_point",
              e.target.value
            ),
        },
      },
      {
        id: `pattern-${row.id ?? row.tempId}`,
        element: {
          type: "textfield",
          placeholder: "Enter URL Pattern",
          value: row.pattern || "",
          onChange: (e) =>
            handleFieldChange(
              setUrlPatternData,
              row.id ?? row.tempId,
              "pattern",
              e.target.value
            ),
        },
      },
      {
        id: `penalty-${row.id ?? row.tempId}`,
        element: {
          type: "textfield",
          placeholder: "Enter Penalty (e.g. 0.5)",
          value: row.penalty || "",
          onChange: (e) =>
            handleFieldChange(
              setUrlPatternData,
              row.id ?? row.tempId,
              "penalty",
              e.target.value
            ),
        },
      },
      {
        id: `delete-icon-${row.id ?? row.tempId}`,
        element: {
          type: "icon",
          icon: <DeleteIcon style={{ width: 18, height: 18 }} />,
          title: "Delete row",
          onClick: () =>
            handleDeleteRow(setUrlPatternData, row.id ?? row.tempId),
        },
      },
    ]);

  // 2) Page HTML/PDF
  const pageHtmlPdfHeaders = [
    { id: "trigger_point", columnData: "Site" },
    { id: "finetuning_type", columnData: "Type" },
    { id: "pattern", columnData: "Pattern" },
    { id: "penalty", columnData: "Penalty" },
    { id: "actions", columnData: "Actions" },
  ];

  const pageHtmlPdfTableContents = pageHtmlPdfData
    .filter((row) => !row.deleted)
    .map((row) => [
      {
        id: `trigger_point-${row.id ?? row.tempId}`,
        element: {
          type: "textfield",
          placeholder: "Enter Site",
          value: row.trigger_point || "",
          maxWidth: "100px",
          minWidth: "100px",
          onChange: (e) =>
            handleFieldChange(
              setPageHtmlPdfData,
              row.id ?? row.tempId,
              "trigger_point",
              e.target.value
            ),
        },
      },
      {
        id: `finetuning_type-${row.id ?? row.tempId}`,
        element: {
          type: "selector",
          defaultValue: row.finetuning_type || "html_weightage",
          value: row.finetuning_type || "",
          items: [
            { value: "html_weightage", title: "HTML Weightage" },
            { value: "pdf_weightage", title: "PDF Weightage" },
          ],
          setValue: (newVal) =>
            handleFieldChange(
              setPageHtmlPdfData,
              row.id ?? row.tempId,
              "finetuning_type",
              newVal
            ),
        },
      },
      {
        id: `pattern-${row.id ?? row.tempId}`,
        element: {
          type: "textfield",
          placeholder: "Enter Pattern",
          value: row.pattern || "",
          onChange: (e) =>
            handleFieldChange(
              setPageHtmlPdfData,
              row.id ?? row.tempId,
              "pattern",
              e.target.value
            ),
        },
      },
      {
        id: `penalty-${row.id ?? row.tempId}`,
        element: {
          type: "textfield",
          placeholder: "Enter Penalty (e.g. 0.5)",
          value: row.penalty || "",
          onChange: (e) =>
            handleFieldChange(
              setPageHtmlPdfData,
              row.id ?? row.tempId,
              "penalty",
              e.target.value
            ),
        },
      },
      {
        id: `delete-icon-${row.id ?? row.tempId}`,
        element: {
          type: "icon",
          icon: <DeleteIcon style={{ width: 18, height: 18 }} />,
          title: "Delete row",
          onClick: () =>
            handleDeleteRow(setPageHtmlPdfData, row.id ?? row.tempId),
        },
      },
    ]);

  // 3) HTML Tags
  const htmlTagsHeaders = [
    { id: "trigger_point", columnData: "Site" },
    { id: "metadata", columnData: "Metadata (JSON)" },
    { id: "actions", columnData: "Actions" },
  ];

  const htmlTagsTableContents = htmlTagsData
    .filter((row) => !row.deleted)
    .map((row) => [
      {
        id: `trigger_point-${row.id ?? row.tempId}`,
        element: {
          type: "textfield",
          placeholder: "Enter Site",
          value: row.trigger_point || "",
          maxWidth: "100px",
          minWidth: "100px",
          onChange: (e) =>
            handleFieldChange(
              setHtmlTagsData,
              row.id ?? row.tempId,
              "trigger_point",
              e.target.value
            ),
        },
      },
      {
        id: `metadata-${row.id ?? row.tempId}`,
        element: {
          type: "textarea",
          placeholder: "Enter metadata JSON",
          value:
            typeof row.metadata === "string"
              ? row.metadata
              : JSON.stringify(row.metadata, null, 2),
          onChange: (e) =>
            handleFieldChange(
              setHtmlTagsData,
              row.id ?? row.tempId,
              "metadata",
              e.target.value
            ),
        },
      },
      {
        id: `delete-icon-${row.id ?? row.tempId}`,
        element: {
          type: "icon",
          icon: <DeleteIcon style={{ width: 18, height: 18 }} />,
          title: "Delete row",
          onClick: () => handleDeleteRow(setHtmlTagsData, row.id ?? row.tempId),
        },
      },
    ]);

  /////////////////////////////////////////////////////////////////////////////
  // Render
  /////////////////////////////////////////////////////////////////////////////
  if (loading) {
    return <TabularLoadingPlaceholder numRows={6} numCols={4} />;
  }
  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <>
      <PageHeader>AI Finetuning</PageHeader>

      {/* 1) URL Pattern Table */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "20px 0",
          width: "100%",
        }}
      >
        <SubtitleM>URL Pattern Based Weightage</SubtitleM>
        <Button variant="PrimaryButtonBlue" onClick={handleAddUrlPattern}>
          Add
        </Button>
      </div>
      <Table
        rowHeaders={urlPatternHeaders}
        tableContents={urlPatternTableContents}
        emptyText="No URL pattern finetuning records found."
        customStyle={{ maxHeight: "300px", overflow: "auto" }}
      />

      {/* 2) Page HTML/PDF Table */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "20px 0",
          width: "100%",
        }}
      >
        <SubtitleM>Page HTML/PDF Based Weightage</SubtitleM>
        <Button variant="PrimaryButtonBlue" onClick={handleAddPageHtmlPdf}>
          Add
        </Button>
      </div>
      <Table
        rowHeaders={pageHtmlPdfHeaders}
        tableContents={pageHtmlPdfTableContents}
        emptyText="No HTML/PDF finetuning records found."
        customStyle={{ maxHeight: "300px", overflow: "auto" }}
      />

      {/* 3) HTML Tags Table */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "20px 0",
          width: "100%",
        }}
      >
        <SubtitleM>Clean up HTML Tags</SubtitleM>
        <Button variant="PrimaryButtonBlue" onClick={handleAddHtmlTagsCleanup}>
          Add
        </Button>
      </div>
      <Table
        rowHeaders={htmlTagsHeaders}
        tableContents={htmlTagsTableContents}
        emptyText="No HTML tag cleanup records found."
        customStyle={{ maxHeight: "300px", overflow: "auto" }}
      />

      <BottomActionBar disabledSave={disabledSave} handleSave={handleSave} />
    </>
  );
}
